import * as XLSX from 'xlsx';
import {
    calculateRowAmounts,
    applyMarginFromItemDetailsToLine,
    resolveLineNoDecPl,
    round2
} from '../../item-details/item-utils';
import {
    STOCK_CHECK_EXCLUDED_ITEMS,
    applyRunningStockCheck,
    buildStockLedger,
    quantityCommittedFromLine
} from '../../item-details/utils/order-line-stock.util';

export {
    STOCK_CHECK_EXCLUDED_ITEMS,
    applyRunningStockCheck,
    buildStockLedger,
    quantityCommittedFromLine
};

export const EXCEL_TEMPLATE_HEADERS = [
    'Item No (ITNO) mandatory',
    'Quantity (ORQT) mandatory',
    'Sales Price (SAPR)'
] as const;

const HEADER_ALIASES: Record<string, keyof ParsedExcelRow> = {
    ITNO: 'itno',
    ITEM: 'itno',
    'ITEM NO': 'itno',
    'ITEM NUMBER': 'itno',
    'ITEM NO (ITNO) MANDATORY': 'itno',
    ORQT: 'orqt',
    QTY: 'orqt',
    QUANTITY: 'orqt',
    'ORDER QUANTITY': 'orqt',
    'QUANTITY (ORQT) MANDATORY': 'orqt',
    SAPR: 'sapr',
    RATE: 'sapr',
    'SALES PRICE': 'sapr',
    'SALES PRICE (SAPR)': 'sapr',
    'SALE PRICE': 'sapr'
};

export interface ParsedExcelRow {
    excelRow: number;
    itno: string;
    orqt: number | null;
    sapr: number | null;
}

export interface ExcelSkippedRow {
    excelRow: number;
    itno: string;
    reason: string;
}

export interface ExcelImportResult {
    imported: any[];
    skipped: ExcelSkippedRow[];
    stockWarnings: number;
}

export interface ExcelImportContext {
    company: string;
    division: string;
    cuno: string;
    whlo: string;
    defaultPriceList: string;
    shouldFetchVatPercentage: boolean;
    ruleActiveForDivision: boolean;
    isCreditNoteOrder: boolean;
    existingItemList: any[];
    startLineNumber: number;
    draftOrderKey: string;
}

function normalizeHeader(value: unknown): string {
    return String(value ?? '')
        .trim()
        .toUpperCase()
        .replace(/\s+/g, ' ');
}

/**
 * Parses ORQT / SAPR from Excel.
 * Comma is the decimal separator (euros + cents), e.g.:
 *   "123,345" → 123.345
 *   "38,484"  → 38.484
 *   "12,4525" → 12.4525
 * Extra commas before the last one are treated as grouping
 * (e.g. "1,23,234" → 123.234).
 * Mixed forms: "1.234,56" (EU) → 1234.56; "1,234.56" (US) → 1234.56.
 */
export function parseExcelNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }
    let s = String(value)
        .trim()
        .replace(/\s/g, '')
        .replace(/€/g, '');
    if (!s) {
        return null;
    }

    const lastComma = s.lastIndexOf(',');
    const lastDot = s.lastIndexOf('.');

    if (lastComma > lastDot) {
        // European: last comma is decimal; dots (and earlier commas) are grouping
        s = s.replace(/\./g, '');
        const idx = s.lastIndexOf(',');
        s = s.slice(0, idx).replace(/,/g, '') + '.' + s.slice(idx + 1);
    } else if (lastDot > lastComma) {
        // US: last dot is decimal; commas are grouping
        s = s.replace(/,/g, '');
    } else if (lastComma >= 0) {
        // Only commas: last comma is decimal (cents)
        const idx = lastComma;
        s = s.slice(0, idx).replace(/,/g, '') + '.' + s.slice(idx + 1);
    }

    const n = Number(s);
    return Number.isFinite(n) ? n : null;
}

function parseOptionalNumber(value: unknown): number | null {
    return parseExcelNumber(value);
}

function rowIsEmpty(values: unknown[]): boolean {
    return values.every((v) => v === null || v === undefined || String(v).trim() === '');
}

function resolveHeaderKey(normalized: string): keyof ParsedExcelRow | null {
    if (HEADER_ALIASES[normalized]) {
        return HEADER_ALIASES[normalized];
    }
    if (normalized.includes('(ITNO)')) {
        return 'itno';
    }
    if (normalized.includes('(ORQT)')) {
        return 'orqt';
    }
    if (normalized.includes('(SAPR)')) {
        return 'sapr';
    }
    return null;
}

export function mapHeadersToColumns(headerRow: unknown[]): Record<keyof ParsedExcelRow, number> | null {
    const mapping: Partial<Record<keyof ParsedExcelRow, number>> = {};
    headerRow.forEach((cell, index) => {
        const key = resolveHeaderKey(normalizeHeader(cell));
        if (key && mapping[key] === undefined) {
            mapping[key] = index;
        }
    });
    if (mapping.itno === undefined || mapping.orqt === undefined) {
        return null;
    }
    return mapping as Record<keyof ParsedExcelRow, number>;
}

export function parseExcelWorkbook(data: ArrayBuffer): ParsedExcelRow[] {
    const workbook = XLSX.read(data, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) {
        throw new Error('Excel file has no worksheets.');
    }
    const sheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
        header: 1,
        defval: '',
        // Use formatted cell text so "123,345" stays a string (euros.cents)
        // instead of Excel converting commas as thousand separators.
        raw: false
    }) as unknown[][];
    if (!rows.length) {
        throw new Error('Excel worksheet is empty.');
    }

    const headerIndex = rows.findIndex((row) => mapHeadersToColumns(row) !== null);
    if (headerIndex < 0) {
        throw new Error('Required columns Item No (ITNO) and Quantity (ORQT) were not found in the first sheet.');
    }

    const columns = mapHeadersToColumns(rows[headerIndex])!;
    const parsed: ParsedExcelRow[] = [];

    for (let i = headerIndex + 1; i < rows.length; i++) {
        const row = rows[i] || [];
        if (rowIsEmpty(row)) {
            continue;
        }
        const itno = String(row[columns.itno] ?? '').trim();
        const orqt = parseOptionalNumber(row[columns.orqt]);
        const sapr = columns.sapr !== undefined ? parseOptionalNumber(row[columns.sapr]) : null;
        parsed.push({
            excelRow: i + 1,
            itno,
            orqt,
            sapr
        });
    }

    return parsed;
}

export function validateParsedRow(row: ParsedExcelRow): string | null {
    if (!row.itno) {
        return 'Item No (ITNO) is required.';
    }
    if (row.orqt === null || row.orqt <= 0) {
        return 'Quantity (ORQT) must be greater than 0.';
    }
    if (row.sapr !== null && (row.sapr <= 0 || !Number.isFinite(row.sapr))) {
        return 'Sales Price (SAPR) must be greater than 0 when provided.';
    }
    return null;
}

export function resolveRateFromExcelAndApi(
    sapr: number | null,
    defaultPriceList: string,
    lstPriceList: any[] | undefined
): number {
    if (sapr !== null && sapr > 0) {
        return round2(sapr);
    }
    const target = (defaultPriceList || '').trim().toUpperCase();
    const found = (lstPriceList || []).find((pl) => String(pl?.ODPRRF ?? '').trim().toUpperCase() === target);
    const rate = found ? Number(found.ODSAPR) : 0;
    return Number.isFinite(rate) ? round2(rate) : 0;
}

export function resolveItemNameFromItemDetails(details: any): string {
    const itemDetails = details?.ItemDetails;
    if (!itemDetails) {
        return '';
    }
    const itemName = String(itemDetails.itemName ?? '').trim();
    if (itemName) {
        return itemName;
    }
    const description = String(itemDetails.itemDescription ?? '').trim();
    const size = String(itemDetails.size ?? '').trim();
    if (description && size) {
        return `${description} ${size}`;
    }
    return description || size;
}

const RELEASED_ITEM_STATUS = '20';

export function normalizeItemStatus(value: unknown): string {
    const raw = String(value ?? '').trim();
    if (!raw) {
        return '';
    }
    const parsed = Number.parseInt(raw, 10);
    if (Number.isFinite(parsed)) {
        return String(parsed).padStart(2, '0');
    }
    return raw.padStart(2, '0');
}

export function getItemStatusShortLabel(status: string): string {
    const code = normalizeItemStatus(status);
    if (!code) {
        return 'Unknown status';
    }

    const known: Record<string, string> = {
        '05': 'Template item',
        '10': 'Preliminary item',
        '15': 'Replacement item',
        '20': 'Released item',
        '30': 'Alternate items available',
        '40': 'Low turnover',
        '50': 'Scheduled for removal',
        '80': 'No longer stocked (returns allowed)',
        '90': 'No longer stocked',
        '99': 'Replaced / no longer stocked'
    };
    if (known[code]) {
        return known[code];
    }

    const numeric = Number.parseInt(code, 10);
    if (Number.isFinite(numeric)) {
        if ((numeric >= 0 && numeric <= 4) || (numeric >= 6 && numeric <= 9) || (numeric >= 11 && numeric <= 14)) {
            return 'Unreleased (user-defined)';
        }
    }

    return 'Unknown status';
}

export function getItemStatusRejectionMessage(details: any): string | null {
    const scrp = details?.lstCoLnScrp;
    if (!scrp || typeof scrp !== 'object') {
        return 'Item status could not be verified.';
    }

    const mbstat = normalizeItemStatus(scrp.MBSTAT);
    const mmstat = normalizeItemStatus(scrp.MMSTAT);
    if (!mbstat || !mmstat) {
        return 'Item status could not be verified.';
    }

    if (mbstat === RELEASED_ITEM_STATUS && mmstat === RELEASED_ITEM_STATUS) {
        return null;
    }

    if (mbstat === mmstat) {
        const label = getItemStatusShortLabel(mbstat);
        return `${label} (status ${mbstat}) — not released for ordering.`;
    }

    return `Not released for ordering (MBSTAT ${mbstat}, MMSTAT ${mmstat}).`;
}

export function buildImportedOrderLine(
    parsed: ParsedExcelRow,
    lineNumber: number,
    itemName: string,
    rate: number,
    detailsResponse: any,
    ctx: ExcelImportContext
): any {
    const itemDetails = detailsResponse?.ItemDetails || {};
    const vat = ctx.shouldFetchVatPercentage ? Number(detailsResponse?.vatFields?.VTP1 ?? 0) : 0;

    const row: any = {
        orderNumber: ctx.draftOrderKey,
        lineNumber: String(lineNumber),
        itemNumber: parsed.itno,
        itemName,
        quantity: parsed.orqt,
        rate,
        discount: ctx.division === '400' ? 0 : 0,
        vat,
        pcs: itemDetails.pcs,
        size: itemDetails.size,
        grossWeight: itemDetails.grossWeight,
        netWeight: itemDetails.netWeight,
        vatCode: itemDetails.vatCode,
        itemGroup: itemDetails.itemGroup,
        ar: Number(itemDetails.STCD ?? 0),
        status: 10,
        originalStatus: 10,
        noDecPl: resolveLineNoDecPl(itemDetails, 2),
        recommendationAddSource: 'EXCEL',
        recommendationWasRecommended: null,
        recommendationRank: null,
        recommendationRunId: null
    };

    applyMarginFromItemDetailsToLine(
        row,
        detailsResponse,
        ctx.division,
        ctx.defaultPriceList,
        Number(parsed.orqt ?? 0)
    );

    return calculateRowAmounts(row);
}

export function downloadExcelTemplate(): void {
    const sheet = XLSX.utils.aoa_to_sheet([Array.from(EXCEL_TEMPLATE_HEADERS)]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Articles');
    XLSX.writeFile(workbook, 'torna-order-articles-template.xlsx');
}

export function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            if (reader.result instanceof ArrayBuffer) {
                resolve(reader.result);
            } else {
                reject(new Error('Could not read file.'));
            }
        };
        reader.onerror = () => reject(reader.error ?? new Error('Could not read file.'));
        reader.readAsArrayBuffer(file);
    });
}
